import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios from 'axios';

import {
  requestCreateAccountabilityTemplate,
  requestViewAccountabilityTemplate,
  requestUpdateAccountabilityTemplate,
  requestGenerateAccountabilityTemplateSessions
} from './accountability-template.actions';
import { requestViewAllProgrammes, requestViewProgramme } from '../programme/programme.actions';
import {
  getCreateAccountabilityTemplateRequest,
  getViewAccountabilityTemplateRequest,
  getUpdateAccountabilityTemplateRequest,
  getGenerateAccountabilityTemplateSessionRequest
} from './accountability-template.endpoints';

import { addSystemNotice, addSpinner, removeSpinner } from '../../slices/system/system.slice';
import { setAccountabilityTemplate } from '../../slices/programme/accountability/accountability.slice';

import { SUCCESS, ERROR } from '../../utils/constants';
import { getErrorMessage } from '../../utils/api-util';
import { stripEmptyTemplates } from '../../utils/accountability-util';

export function* performCreateAccountabilityTemplateRequest({ payload }) {
  const { input, onSuccess } = payload;

  try {
    yield put(addSpinner('Creating Accountability Template'));

    const [endpoint, options] = getCreateAccountabilityTemplateRequest(stripEmptyTemplates(input));

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(removeSpinner('Creating Accountability Template'));
    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(requestViewProgramme({ externalReference: input.programmeExternalReference }));
    yield put(requestViewAccountabilityTemplate({ externalReference: input.programmeExternalReference }));
    yield put(requestViewAllProgrammes({}));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Creating Accountability Template'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForCreateAccountabilityTemplateRequest() {
  yield takeLatest(requestCreateAccountabilityTemplate.type, performCreateAccountabilityTemplateRequest);
}

export function* performViewAccountabilityTemplateRequest({ payload }) {
  const { externalReference, onSuccess } = payload;

  try {
    yield put(addSpinner('Retrieving Accountability Template'));

    const [endpoint, options] = getViewAccountabilityTemplateRequest(externalReference);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(setAccountabilityTemplate(data.accountabilityTemplates[0]));
    yield put(removeSpinner('Retrieving Accountability Template'));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Retrieving Accountability Template'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForViewAccountabilityTemplateRequest() {
  yield takeLatest(requestViewAccountabilityTemplate.type, performViewAccountabilityTemplateRequest);
}

export function* performUpdateAccountabilityTemplateRequest({ payload }) {
  const { input, onSuccess } = payload;

  try {
    yield put(addSpinner('Updating Accountability Template'));

    const [endpoint, options] = getUpdateAccountabilityTemplateRequest(stripEmptyTemplates(input));

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(removeSpinner('Updating Accountability Template'));
    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(requestViewAccountabilityTemplate({ externalReference: input.programmeExternalReference }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Updating Accountability Template'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForUpdateAccountabilityTemplateRequest() {
  yield takeLatest(requestUpdateAccountabilityTemplate.type, performUpdateAccountabilityTemplateRequest);
}

export function* performGenerateAccountabilityTemplateSessionsRequest({ payload }) {
  const { input, onSuccess } = payload;

  try {
    yield put(addSpinner('Generating Sessions'));

    const [endpoint, options] = getGenerateAccountabilityTemplateSessionRequest(input);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, data.sessionTemplates);
    }

    yield put(removeSpinner('Generating Sessions'));
    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Generating Sessions'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForGenerateAccountabilityTemplateSessionsRequest() {
  yield takeLatest(
    requestGenerateAccountabilityTemplateSessions.type,
    performGenerateAccountabilityTemplateSessionsRequest
  );
}

export default function* accountabilityTemplateSaga() {
  yield all([
    watchForCreateAccountabilityTemplateRequest(),
    watchForViewAccountabilityTemplateRequest(),
    watchForUpdateAccountabilityTemplateRequest(),
    watchForGenerateAccountabilityTemplateSessionsRequest()
  ]);
}
